<template>
  <div class="exchage-history-table">
    <table-with-filters
      :show-load-more="showLoadMore && !fetching"
      :loading="loading"
      :fetching="fetching"
      @loadMore="loadMore"
    >
      <template #filters>
        <h2
          class="exchage-history-table__header txt--bold txt--22px txt--26px--l txt--height-30px txt--height-34px--l txt--color-04 mb-0"
        >
          {{ $t('buySellCrypto.history.title') }}
        </h2>
      </template>
      <template #table>
        <exc-table
          :heading="columns"
          :rows="rows"
          :empty-message="$t('trade.noData')"
          adaptive
          class="exchage-history-table__table"
        >
          <template #td-custom="{ pair, status }">
            <div class="exchage-history-table__status-with-pair">
              <span class="exchage-history-table__pair">
                {{ pair }}
              </span>
              <div
                class="exchage-history-table__status"
                :class="{
                  'exchage-history-table__status--orange': status.type.orange,
                  'exchage-history-table__status--green': status.type.green,
                  'exchage-history-table__status--red': status.type.red,
                  'exchage-history-table__status--grey': status.type.grey,
                  'exchage-history-table__status--violet': status.type.violet,
                }"
              >
                <div class="exchage-history-table__status-dot" />
                <span
                  class="exchage-history-table__status-text txt--bold txt--14px txt--height-18px"
                >
                  {{ status.value }}
                </span>
              </div>
            </div>
          </template>
          <template #td-status="{ status }">
            <div
              class="exchage-history-table__status"
              :class="{
                'exchage-history-table__status--orange': status.type.orange,
                'exchage-history-table__status--green': status.type.green,
                'exchage-history-table__status--red': status.type.red,
                'exchage-history-table__status--grey': status.type.grey,
                'exchage-history-table__status--violet': status.type.violet,
              }"
            >
              <div class="exchage-history-table__status-dot" />
              <span
                class="exchage-history-table__status-text txt--bold txt--14px txt--height-18px"
              >
                {{ status.value }}
              </span>
            </div>
          </template>
          <template #td-hash="{ hash, link }">
            <copy-hash-and-link :hash="hash" :link="link" @copy="copyHash">
              <template #icon>
                <copy-icon width="12" height="15" />
              </template>
            </copy-hash-and-link>
          </template>
          <template #td-address="{ address }">
            <copy-hash-and-link :hash="address" @copy="copyHash">
              <template #icon>
                <copy-icon width="12" height="15" />
              </template>
            </copy-hash-and-link>
          </template>
        </exc-table>
      </template>
    </table-with-filters>
  </div>
</template>

<script>
import { ExcTable } from '@/components/common'
import {
  copyToClipboard,
  formatShortDateWithTime,
  parseNumber,
} from '@/lib/utils'
import { TRADE_TYPE } from '@/lib/constants'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'ExchangeLatamexHistory',
  components: {
    ExcTable,
    CopyHashAndLink: () =>
      import(
        /* webpackMode: "eager" */ '@/components/clickable/CopyHashAndLink.vue'
      ),
    CopyIcon: () =>
      import(/* webpackMode: "eager" */ '@/assets/icons/copy.svg?inline'),
    TableWithFilters: () =>
      import('@/components/ui-kit/table/TableWithFiltersNew.vue'),
  },
  data () {
    return {
      currentPage: 1,
      loading: false,
      fetching: true, // for page fetching
    }
  },
  computed: {
    ...mapState({
      historyRows: state => state.latamexExchange.history,
      showLoadMore: state => state.latamexExchange.loadMoreHistory,
    }),
    columns () {
      return {
        custom: '',
        time: this.$t('table.date'),
        pair: {
          value: this.$t('trade.orders.pair'),
          hideOnMobile: true,
        },
        type: this.$t('trade.orders.type'),
        price: this.$t('trade.orders.price'),
        amount: this.$t('history.historyDeposit.amount'),
        fee: this.$t('trade.orders.fee'),
        total: this.$t('trade.orders.total'),
        hash: this.$t('table.transactionHash'),
        status: {
          value: this.$t('table.status'),
          hideOnMobile: true,
        },
      }
    },
    rows () {
      return this.historyRows.map((t) => {
        const parsedTickers = t.pair.split('/')
        const feeTicker =
          t.side === TRADE_TYPE.BUY ? parsedTickers[0] : parsedTickers[1]

        return {
          time: formatShortDateWithTime(t.createdAt * 1000),
          pair: t.pair,
          type: {
            value: this.$t(`history.ordersHistory.tradeType.${t.side}`),
            class: t.side === TRADE_TYPE.BUY ? 'txt--green' : 'txt--red',
          },
          price: parseNumber(t.price),
          amount: parseNumber(t.amount),
          fee: `${parseNumber(t.fee)} ${feeTicker}`,
          total: parseNumber(t.total),
          hash: t.hash || '',
          status: {
            value: this.$t(
              `buySellCrypto.history.statuses.${t.status.toLowerCase()}`,
            ),
            type: {
              orange: t.status === 'Pending',
              green: t.status === 'Accepted',
              red: t.status === 'Rejected',
              grey: t.status === 'Expired',
              violet: t.status === 'Incomplete',
            },
            class: 'exchage-history-table__badge-cell txt--on-one-row',
          },
        }
      })
    },
  },
  async created () {
    if (!process.client) return

    this.fetching = true
    await this.getLatamexHistory({ page: 1 })
    this.fetching = false
  },
  methods: {
    ...mapActions({
      getLatamexHistory: 'latamexExchange/getLatamexHistory',
    }),
    async loadMore () {
      if (!this.showLoadMore) return

      this.loading = true
      this.currentPage += 1
      await this.getLatamexHistory({
        page: this.currentPage,
      })
      this.loading = false
    },
    copyHash (hash) {
      copyToClipboard(hash).then(
        () => {
          this.$notify({
            // group: "info",
            type: 'success',
            title: this.$t('common.notify.copied'),
            text: this.$t('common.notify.copied'),
            // text: i18n.t("notify.copiedReferralLink"),
          })
        },
        () => {
          this.$notify({
            // group: "error",
            type: 'error',
            title: this.$t('common.notify.uhOh'),
            text: this.$t('common.notify.somethingWentWrong'),
          })
        },
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.exchage-history-table {
  $root: &;

  padding: toRem(16px) toRem(14px);
  background: color('color-01');
  border-radius: toRem(12px);
  box-shadow: map-get($shadows, '04');

  @include bp(l) {
    padding: toRem(24px);
  }

  ::v-deep {
    #{$root}__badge-cell {
      @include bp($between-l-xl) {
        width: 1%;
      }
    }

    .tdx-table__container--adaptive {
      padding: 0;
      background: none;
    }

    .exc-table__cell-custom {
      background: rgba(145, 148, 159, 0.04);
    }

    @include bp(0, $between-l-xl) {
      .exc-table__cell-pair {
        display: none;
      }

      .exc-table__cell-status {
        display: none;
      }
    }

    @include bp($between-l-xl) {
      .exc-table__cell-custom {
        display: none;
      }
    }
  }

  &__status {
    display: flex;
    align-items: center;
    gap: toRem(4px);

    &--orange {
      color: color('color-12');

      #{$root}__status-dot {
        background: color('color-12');
      }
    }

    &--green {
      color: color('color-06');

      #{$root}__status-dot {
        background: color('color-06');
      }
    }

    &--red {
      color: color('color-07');

      #{$root}__status-dot {
        background: color('color-07');
      }
    }

    &--grey {
      color: color('color-03');

      #{$root}__status-dot {
        background: color('color-03');
      }
    }

    &--violet {
      color: color('color-10');

      #{$root}__status-dot {
        background: color('color-10');
      }
    }
  }

  &__status-dot {
    width: toRem(6px);
    height: toRem(6px);
    border-radius: 50%;
  }

  &__status-with-pair {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
}
</style>
